<template>
  <div class="relative" ref="root">
    <div class="relative" ref="inputContainer">
      <input
        :value="displayValue"
        @focus="!disabled && (open = true)"
        @click="!disabled && (open = true)"
        readonly
        :disabled="disabled"
        :class="[
          'w-full border rounded-lg pr-9 pl-2 sm:pl-3 py-2 text-sm focus:outline-none',
          disabled
            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent'
        ]"
        :placeholder="placeholder"
      />
      <button
        type="button"
        @click="!disabled && toggle()"
        :disabled="disabled"
        :class="[
          'absolute inset-y-0 right-0 px-2',
          disabled
            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        ]"
      >
        <CalendarIcon class="w-5 h-5" />
      </button>
    </div>
    <Teleport to="body">
      <div
        v-if="open"
        ref="calendarEl"
        :style="calendarStyle"
        class="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg dark:shadow-gray-900/50 p-3 w-72"
      >
        <div class="flex items-center justify-between mb-2">
          <button
            @click="prevMonth"
            class="px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-300"
            aria-label="Vorige maand"
          >
            ‹
          </button>
          <div class="text-sm font-medium text-gray-900 dark:text-white">
            {{ monthLabel }} {{ viewYear }}
          </div>
          <button
            @click="nextMonth"
            class="px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-300"
            aria-label="Volgende maand"
          >
            ›
          </button>
        </div>
        <div
          class="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 dark:text-gray-400 mb-1"
        >
          <div v-for="d in ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo']" :key="d">{{ d }}</div>
        </div>
        <div class="grid grid-cols-7 gap-1">
          <div v-for="n in startOffset" :key="'o' + n"></div>
          <button
            v-for="d in daysInMonth"
            :key="d"
            class="py-1.5 text-sm rounded hover:bg-blue-50 dark:hover:bg-blue-900/50 dark:text-gray-200"
            :class="{
              'bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-600': isSelected(d)
            }"
            @click="selectDay(d)"
          >
            {{ d }}
          </button>
        </div>
        <div class="mt-3 flex justify-end">
          <button
            @click="closePopover"
            class="px-3 py-1.5 text-sm rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 dark:text-gray-200"
          >
            Sluiten
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { CalendarIcon } from '@heroicons/vue/24/outline'
import { useElementBounding } from '@vueuse/core'

const props = defineProps({
  modelValue: { type: String, default: '' }, // YYYY-MM-DD
  placeholder: { type: String, default: 'DD-MM-YYYY' },
  disabled: { type: Boolean, default: false },
  autoOpen: { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue', 'close'])

const open = ref(false)

const closePopover = () => {
  if (open.value) emit('close')
  open.value = false
}
const root = ref(null)
const inputContainer = ref(null)
const calendarEl = ref(null)

// Track input container position for calendar positioning
const inputBounds = useElementBounding(inputContainer)

const calendarStyle = computed(() => ({
  top: `${inputBounds.bottom.value + 8}px`,
  left: `${inputBounds.left.value}px`
}))

const today = new Date()
const parse = s => {
  if (!s) return null
  const d = new Date(`${s}T00:00:00`)
  return isNaN(d) ? null : d
}

const selected = ref(parse(props.modelValue) || null)
watch(
  () => props.modelValue,
  nv => {
    selected.value = parse(nv)
  }
)

const viewMonthDate = ref(selected.value || new Date(today.getFullYear(), today.getMonth(), 1))
const viewYear = computed(() => viewMonthDate.value.getFullYear())
const viewMonth = computed(() => viewMonthDate.value.getMonth())

const monthNames = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december'
]
const monthLabel = computed(() => monthNames[viewMonth.value])

const firstDayOfMonth = computed(() => new Date(viewYear.value, viewMonth.value, 1))
// Monday start offset: JS getDay() 0=Sun..6=Sat → we want Mon=0..Sun=6
const startOffset = computed(() => {
  const dow = firstDayOfMonth.value.getDay() // 0..6, Sun..Sat
  // convert to Mon-first index
  return (dow + 6) % 7
})
const daysInMonth = computed(() => new Date(viewYear.value, viewMonth.value + 1, 0).getDate())

const displayValue = computed(() => {
  if (!selected.value) return ''
  return selected.value.toLocaleDateString('nl-NL') // dd-mm-jjjj
})

const isSelected = d => {
  if (!selected.value) return false
  return (
    selected.value.getFullYear() === viewYear.value &&
    selected.value.getMonth() === viewMonth.value &&
    selected.value.getDate() === d
  )
}

const pad2 = n => String(n).padStart(2, '0')
const toYMDLocal = date =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

const selectDay = d => {
  // Build date in local time and serialize to YYYY-MM-DD without timezone shifts
  const dt = new Date(viewYear.value, viewMonth.value, d)
  const ymd = toYMDLocal(dt)
  emit('update:modelValue', ymd)
  selected.value = dt
  open.value = false
}

const toggle = () => {
  open.value = !open.value
}

const prevMonth = () => {
  const y = viewYear.value
  const m = viewMonth.value
  viewMonthDate.value = new Date(y, m - 1, 1)
}
const nextMonth = () => {
  const y = viewYear.value
  const m = viewMonth.value
  viewMonthDate.value = new Date(y, m + 1, 1)
}

const onClickOutside = e => {
  if (!root.value) return
  // Check both root and teleported calendar element
  if (!root.value.contains(e.target) && !calendarEl.value?.contains(e.target)) {
    closePopover()
  }
}
onMounted(() => {
  document.addEventListener('click', onClickOutside)
  if (props.autoOpen && !props.disabled) {
    open.value = true
  }
})
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))
</script>
